import type { LucideIcon } from 'lucide-react';
import { BookOpen, Landmark, Layers, LifeBuoy } from 'lucide-react';
import type { AIChatOpenSource } from '@/contexts/AIChatContext';

export type StarterPromptIcon = 'search' | 'bug' | 'zap' | 'flag';

export interface StarterPrompt {
  /** Stable identifier for routing — use this instead of matching on display text */
  id?: string;
  text: string;
  icon: StarterPromptIcon;
}

export interface AgentConfig {
  /** Must match AIChatOpenSource values */
  source: AIChatOpenSource;
  /** Display name in dropdown and badge */
  label: string;
  /** Short description shown in dropdown */
  description: string;
  /** Icon for badge and dropdown */
  icon: LucideIcon;
  /** Whether this agent appears in the switcher dropdown */
  switchable: boolean;
  /** Starter prompts shown in the welcome screen */
  starterPrompts: StarterPrompt[];
}

export const AGENTS: AgentConfig[] = [
  {
    source: 'settings',
    label: 'Settings guide',
    description: 'How settings flow from operator to client to debt type',
    icon: Layers,
    switchable: false,
    starterPrompts: [],
  },
  {
    source: 'default',
    label: 'General Information',
    description: 'General guidance and help for the platform',
    icon: BookOpen,
    switchable: true,
    starterPrompts: [
      { text: 'How do I navigate the dashboard?', icon: 'search' },
      { text: 'Explain the case lifecycle in Forest.', icon: 'search' },
      { text: 'What reports are available to me?', icon: 'search' },
      { id: 'open-support', text: "Something doesn't work", icon: 'bug' },
    ],
  },
  {
    source: 'finance',
    label: 'Finance',
    description: 'Bank reconciliation and payouts',
    icon: Landmark,
    switchable: true,
    starterPrompts: [
      { text: 'What payouts or settlements need my attention right now?', icon: 'search' },
      { text: 'Help me understand unmatched bank lines and what to do next.', icon: 'search' },
      { text: 'Summarize collections activity and any anomalies I should review.', icon: 'zap' },
    ],
  },
  {
    source: 'support',
    label: 'Support',
    description: 'Report a bug or submit a need — the agent sorts out which it is',
    icon: LifeBuoy,
    switchable: true,
    starterPrompts: [],
  },
];

export function getAgentConfig(source: AIChatOpenSource | null): AgentConfig {
  return AGENTS.find((a) => a.source === (source ?? 'default')) ?? AGENTS[0];
}
